
import * as R from 'ramda';
import {
  takeLatest,
  put,
  select,
  all,
} from 'redux-saga/effects';
import {
  partner as partnerApis,
  partnerManagement as pmApis,
  ti2 as ti2Apis,
} from '../../../../api.js';
import { apiSaga } from '../../../../root-sagas.js';
import editAppKeySagas from './edit-app-key-configuration-sagas.js';
import productMappingsSagas from './product-mappings-sagas.js';
import extractionSettingsSagas from './extraction-settings-sagas.js';
import llmSettingsSagas from './llm-settings-sagas.js';
import itineraryMiningSagas from './itinerary-mining-sagas.js';
import { _getAccountInfo } from './edit-app-key-configuration-sagas.js';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

const ROUTE_SEGMENTS = new Set([
  'generative-ai', 'partner-settings', 'main',
  'extraction', 'edit-app-key-configuration', 'product-mappings',
  'llm', 'itineraryAssist', 'bookings',
]);

const extractHintOrId = pathname => {
  const segments = pathname.split('/').filter(Boolean);
  const uuid = segments.find(s => UUID_RE.test(s));
  if (uuid) return uuid;
  const last = segments[segments.length - 1];
  if (last && !ROUTE_SEGMENTS.has(last)) return decodeURIComponent(last);
  return undefined;
};

function* onLocationChange({ payload }) {
  const asyncActions = [];
  const pathname = R.pathOr('', ['location', 'pathname'], payload);
  const needsPartners = pathname.includes('generative-ai/partner-settings') || pathname.includes('itineraryassist/context-files');
  if (needsPartners) {
    const { partners } = yield select(state => state.generativeAi.partnerSettings.metadata);
    if (!partners || partners.length === 0) {
      yield apiSaga('genAi_partnerSettings_loadPartners', pmApis.getPartners, []);
    }
  }
  if (pathname.includes('generative-ai/partner-settings') ) {
    const partnerId = extractHintOrId(pathname);
    yield put({ type: 'genAi_partnerSettings_updateProps', payload: { hint: partnerId, partnerId } })
    if (partnerId && UUID_RE.test(partnerId)) {
      asyncActions.push(apiSaga('genAi_partnerSettings_loadPartner', partnerApis.getPartner, [{ partnerId }]));
    }
    const { partners, allAppKeys } = yield select(state => state.generativeAi.partnerSettings.metadata);
    if (!partners || partners.length === 0) {
      asyncActions.push(apiSaga('genAi_partnerSettings_loadPartners', pmApis.getPartners, [{ listBy: 'partner', summaryOnly: true }]));
    }
    if (!allAppKeys || allAppKeys.length === 0) {
      const { companyId, ti2token } = yield _getAccountInfo();
      asyncActions.push(apiSaga('genAi_partnerSettings_loadAllAppKeys', ti2Apis.getAllAppKeys, [{ companyId, ti2token }]));
    }
    if (pathname.includes('edit-app-key-configuration')) {
      yield put({
        type: 'genAi_partnerSettings_updateProps',
        payload: { pageTitle: 'Product Catalog' }
      });
      // after getAllAppKeys, integrationApp will be set in the metadata
      const {
        integrationApp = 'tourplan'
      } = yield select(state => state.generativeAi.partnerSettings.metadata);
      if (integrationApp && partnerId) {
        yield put({
          type: 'genAi_editAppKey_loadInitialData',
          payload: { app: integrationApp, hint: partnerId }
        });
      }
    }
    if (pathname.includes('extraction')) {
      const toolId = pathname.split('/')[4];
      yield put({
        type: 'genAi_partnerSettings_updateProps',
        payload: { pageTitle: 'Extraction Settings', toolId }
      });
      // get parser whether partnerId is present or not
      // when no partnerId, we are getting the company parsers
      yield put({ type: 'genAI_extractionSettings_getLLMParserInJson' });
    }
    if (pathname.includes('/llm/')) {
      const toolId = pathname.split('/')[4];
      yield put({
        type: 'genAi_partnerSettings_updateProps',
        payload: { pageTitle: 'LLM Settings', toolId }
      });
      yield put({ type: 'genAI_llmSettings_initialize' });
    }
    if (pathname.includes('partner-mappings')) {
      yield put({
        type: 'genAi_partnerSettings_updateProps',
        payload: { pageTitle: 'Product Mappings' }
      });
      const { integrationApp } = yield select(state => state.generativeAi.partnerSettings.metadata);
      if (partnerId && integrationApp) {
        yield put({
          type: 'genAi_productMappings_getStagingAIRules',
          payload: { app: integrationApp, hint: partnerId }
        });
      }
    }
    if (pathname.includes('/partner-settings/main')) {
      yield put({
        type: 'genAi_partnerSettings_updateProps',
        payload: { pageTitle: 'Main' },
      });
      yield put({ type: 'genAi_itineraryMining_init' });
    }
  }
  if (asyncActions.length > 0) {
    yield all(asyncActions);
  }
}

// eslint-disable-next-line import/no-anonymous-default-export
export default [
  takeLatest('@@router/LOCATION_CHANGE', onLocationChange),
  ...editAppKeySagas,
  ...productMappingsSagas,
  ...extractionSettingsSagas,
  ...llmSettingsSagas,
  ...itineraryMiningSagas,
];
